import { useCallback, useEffect, useState } from "react";
import { Building2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";
const blank = { name: "", city: "", contact_email: "", admin_email: "", admin_name: "" };

/** Lab (tenant) registry — each lab's data is completely separate. Super admin only. */
export const LabsCard = ({ onChanged }) => {
  const [labs, setLabs] = useState([]);
  const [form, setForm] = useState({ ...blank });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/labs");
      setLabs(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => String(v || "").trim()));
      const { data } = await api.post("/labs", payload);
      toast.success(data.admin
        ? `Lab created. Admin ${data.admin.email} — temporary password: ${data.admin.temp_password}`
        : "Lab created", { duration: 25000 });
      setForm({ ...blank });
      await load();
      onChanged?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }  };

  const rename = async (lab) => {
    const name = window.prompt("New name for this lab", lab.name);
    if (!name || name === lab.name) return;
    try { await api.put(`/labs/${lab.lab_id}`, { name }); toast.success("Lab renamed"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (lab) => {
    const status = lab.status === "active" ? "suspended" : "active";
    try {
      await api.put(`/labs/${lab.lab_id}`, { status });
      toast.success(`${lab.name} is now ${status}`);
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (lab) => {
    if (!window.confirm(`Delete ${lab.name}? Its users are de-linked and suspended.`)) return;
    const purge = window.confirm("Also permanently delete all of this lab's records and data? "
      + "Click Cancel to keep the data.");
    try {
      await api.delete(`/labs/${lab.lab_id}`, { params: { purge } });
      toast.success(purge ? "Lab and its data deleted" : "Lab deleted, data retained");
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="labs-card">
      <div className="flex items-start gap-3">
        <Building2 className="h-5 w-5 text-[#002FA7] mt-0.5" />
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Labs</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Every lab keeps its own samples, personnel, sub-store and quality records. Users linked to a lab
            share that data, limited by their access packages.
          </p>
        </div>
      </div>

      <form onSubmit={create} className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="create-lab-form">
        <div>
          <Label className={LBL}>Lab name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            required className="mt-1" data-testid="new-lab-name" />
        </div>
        <div>
          <Label className={LBL}>City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="mt-1" data-testid="new-lab-city" />
        </div>
        <div>
          <Label className={LBL}>Lab admin email</Label>
          <Input type="email" value={form.admin_email} data-testid="new-lab-admin-email"
            onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className={LBL}>Lab admin name</Label>
          <Input value={form.admin_name} data-testid="new-lab-admin-name"
            onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={busy} data-testid="create-lab-btn"
            className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">Create lab</Button>
        </div>
      </form>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="labs-table">
          <thead className="bg-[#F3F4F6]">
            <tr>
              {["Lab", "Users", "Records", "Status", ""].map((h) => (
                <th key={h} className="text-left font-medium text-xs uppercase tracking-[0.08em] text-[#6B7280] px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labs.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[#6B7280]" data-testid="labs-empty">
                No labs yet.</td></tr>
            )}
            {labs.map((lab) => (
              <tr key={lab.lab_id} className="border-t border-[#E5E7EB]" data-testid={`lab-row-${lab.lab_id}`}>
                <td className="px-3 py-2">
                  <p className="text-[#111827]">{lab.name}
                    {lab.is_default && <span className="ml-1.5 text-[10px] uppercase text-[#002FA7]">founding</span>}
                  </p>
                  <p className="text-xs text-[#6B7280]">{lab.city || "—"} · {lab.primary_admin || "no lab admin"}</p>
                </td>
                <td className="px-3 py-2 text-xs">{lab.user_count}</td>
                <td className="px-3 py-2 text-xs">{lab.record_count}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggle(lab)} data-testid={`lab-status-${lab.lab_id}`}
                    className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-full border ${
                      lab.status === "active" ? "text-[#2E7D32] border-[#2E7D32]" : "text-[#B58900] border-[#FCC419]"}`}>
                    {lab.status}
                  </button>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => rename(lab)} data-testid={`lab-rename-${lab.lab_id}`}
                    className="text-xs text-[#002FA7] hover:underline mr-3">Rename</button>
                  {!lab.is_default && (
                    <button onClick={() => remove(lab)} data-testid={`lab-delete-${lab.lab_id}`}
                      className="p-1.5 text-[#C92A2A] hover:bg-[#F3F4F6] rounded"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
